import type { Attachment } from '@core/attachments-types';
import type { Visibility } from '@core/visibility-types';

// copy due to circular dependency
enum OfferStatus {
  Draft = 'draft',
  Published = 'published',
  Internal = 'internal',
  Closed = 'closed',
  Archived = 'archived',
}

enum OfferKind {
  Job = 'job',
  TalentPool = 'talent_pool',
}

export type AutomatedActionDueDate =
  | {
      dueDateType: 'relative';
      dueDateUnit: string;
      dueDateValue: number;
      timezone: string;
    }
  | {
      dueDateType: 'absolute';
      dueDateValue: string;
      timezone: string;
    };

export type AutomatedActionOffer = {
  id: number;
  kind: OfferKind;
  location: string;
  slug: string;
  guid: string;
  status: OfferStatus;
  title: string;
  departmentId?: string;
  locationIds?: number[];
};

export type AutomatedAction =
  | {
      kind: AutomatedActionKind.SendEmail;
      data: {
        from: number | null;
        fromKind: EmailSenderKindEnum;
        toAdminIds: number[];
        toCandidate: boolean;
        toHiringManager: boolean;
        toRecruiter: boolean;
        ccAdminIds: number[];
        ccCandidate: boolean;
        ccHiringManager: boolean;
        ccRecruiter: boolean;
        bccAdminIds: number[];
        bccCandidate: boolean;
        bccHiringManager: boolean;
        bccRecruiter: boolean;
        subject: string;
        bodyHtml: string;
        signature: boolean;
        visibility: Visibility;
        sendAfterAtHour?: number | null;
        sendAfterAtMinute?: number | null;
        sendAfterDays?: number | null;
        sendAtTimezone?: string | null;
        attachmentIds: number[];
      };
      id?: number;
      canceled?: boolean;
      references?: { attachments: Attachment[] };
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddNote;
      data: {
        body: string;
        bodyJson: object | null;
        bodyHtml: string;
        visibility: Visibility;
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddFollowers;
      data: {
        adminIds: number[];
        adminIdsAddHiringManager: boolean;
        adminIdsAddRecruiter: boolean;
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddTask;
      data:
        | {
            adminIds: number[];
            dueDateType: 'relative' | null;
            dueDateUnit: string | null;
            dueDateValue: string | null;
            title: string;
            timezone: string | null;
            adminIdsAddAdminPerformingAction: boolean;
            adminIdsAddHiringManager: boolean;
            adminIdsAddRecruiter: boolean;
          }
        | {
            adminIds: number[];
            dueDateType: 'absolute' | null;
            dueDateValue: string | null;
            title: string;
            timezone: string;
            adminIdsAddAdminPerformingAction: boolean;
            adminIdsAddHiringManager: boolean;
            adminIdsAddRecruiter: boolean;
          };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddTags;
      data: {
        tags: string[];
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.EvaluationRequest;
      data: {
        interviewTemplateId: number | null;
        adminIds: number[];
        adminIdsAddHiringManager: boolean;
        adminIdsAddRecruiter: boolean;
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AssignTo;
      data: {
        offerIds: number[];
        talentPoolIds: number[];
      };
      id?: number;
      canceled?: boolean;
      references?: { offers: AutomatedActionOffer[]; talentPools: AutomatedActionOffer[] };
      _destroy?: boolean;
    };

export enum AutomatedActionKind {
  SendEmail = 'send_email',
  AddTags = 'add_tags',
  AddNote = 'add_note',
  AddTask = 'add_task',
  EvaluationRequest = 'request_evaluation',
  AddFollowers = 'add_followers',
  AssignTo = 'assign',
}

export interface AutomatedActionCandidate {
  name: string;
  emails: string[];
  id: number;
  photoThumbUrl: string;
  initials: string;
  hasAvatar: boolean;
  isAnonymous?: boolean;
}

export type Action = {
  actionTemplate: AutomatedAction;
  completedAt: string;
  errors: string[];
  id: number;
  trigger_action_set_id: number;
  canceled: boolean;
};

export type AutomatedActionStage = {
  id: number;
  name: string;
  position: number;
};

export type ActionSet = {
  actions: Action[];
  adminId: number;
  candidate?: AutomatedActionCandidate;
  candidateId: number;
  companyId: number;
  disqualifyReasonId: number;
  id: number;
  offerId: number;
  previousStageId: number;
  previousStage: AutomatedActionStage;
  stageId: number;
  stage: AutomatedActionStage;
  status: ActionSetStatusEnum;
};

export enum ActionSetStatusEnum {
  Completed = 'completed',
  Created = 'created',
  Canceled = 'canceled',
}

export enum EmailSenderKindEnum {
  UserPerformingAction = 'admin_performing_action',
  Recruiter = 'recruiter',
  HiringManager = 'hiring_manager',
  Admin = 'admin',
  SharedProvider = 'shared_email_provider',
}

export type EmailSenderKind = EmailSenderKindEnum | null;
